import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  PlayCircle,
  FileText,
  ChevronRight,
  Search,
  Calendar,
  LayoutDashboard,
  Bell,
  Receipt,
  Package,
  Stethoscope,
  Brain,
  Settings,
  Puzzle,
  Image,
  GraduationCap,
  Smile,
  MessageCircle,
  Users,
  ShieldCheck
} from "lucide-react";

export default function TutorialsPage() {
  const [activeTab, setActiveTab] = useState("all");
  const { user } = useAuth();

  const categories = [
    { id: "all", name: "Todos os Guias" },
    { id: "management", name: "Gestão e Agenda" },
    { id: "clinical", name: "Atendimento Clínico" },
    { id: "integrations", name: "Integrações e Extensões" },
  ];

  const tutorials = [
    {
      title: "Boas Práticas, RDC e fiscalização do CFO",
      category: "management",
      type: "article",
      icon: <ShieldCheck className="text-purple-600" size={24} />,
      desc: "Siga uma ação por vez e organize RDC, roteiro PF/PJ do CFO, documentos, esterilização, equipe, rotinas e fiscalizações recebidas.",
      link: "/ajuda/tutoriais/boas-praticas"
    },
    {
      title: "Usuários e Permissões",
      category: "management",
      type: "article",
      icon: <Users className="text-purple-600" size={24} />,
      desc: "Convide colaboradores, escolha perfis, personalize permissões e configure o acesso de dentistas à agenda e aos pacientes.",
      link: "/ajuda/tutoriais/usuarios-permissoes"
    },
    {
      title: "Segurança da Conta e Auditoria",
      category: "management",
      type: "article",
      icon: <ShieldCheck className="text-emerald-600" size={24} />,
      desc: "Proteja cada acesso com autenticação em dois fatores, revise dispositivos conectados e acompanhe eventos importantes da clínica.",
      link: "/ajuda/tutoriais/seguranca-auditoria"
    },
    {
      title: "Configurações da Clínica",
      category: "management",
      type: "article",
      icon: <Settings className="text-indigo-500" size={24} />,
      desc: "Configure o nome da clínica, logo e identidade visual antes de começar a usar a plataforma.",
      link: "/ajuda/tutoriais/configuracoes"
    },
    {
      title: "Agendamento Inteligente",
      category: "management",
      type: "article",
      icon: <Calendar className="text-indigo-500" size={24} />,
      desc: "Como organizar sua agenda, visualizar atendimentos por dia ou semana e acompanhar status dos pacientes.",
      link: "/ajuda/tutoriais/agendamento"
    },
    {
      title: "WhatsApp Business Integrado",
      category: "integrations",
      type: "article",
      icon: <MessageCircle className="text-green-500" size={24} />,
      desc: "Configure o módulo oficial via API da Meta, entenda a janela de atendimento de 24 horas, acompanhe mensagens lidas e responda pacientes pela Central WhatsApp.",
      link: "/ajuda/tutoriais/whatsapp-business"
    },    
    {
      title: "Lembretes e Acompanhamentos da Clínica",
      category: "management",
      type: "article",
      icon: <Bell className="text-indigo-500" size={24} />,
      desc: "Organize retornos, aniversários, contatos pendentes e tarefas importantes da rotina da clínica.",
      link: "/ajuda/tutoriais/lembretes"
    },
    {
      title: "Assistente Técnico",
      category: "clinical",
      type: "article",
      icon: <Brain className="text-indigo-500" size={24} />,
      desc: "Tire dúvidas técnicas gerais e obtenha apoio textual sem acessar dados dos pacientes.",
      link: "/ajuda/tutoriais/analise-ia"
    },    
    {
      title: "Como preencher o Odontograma Interativo",
      category: "clinical",
      type: "video",
      icon: <PlayCircle className="text-rose-500" size={24} />,
      desc: "Aprenda a registrar a situação inicial, planejar procedimentos e acompanhar a evolução gráfica tratamento por tratamento.",
      link: "/ajuda/tutoriais/odontograma"
    },
    {
      title: "Gerando Receituários e Contratos",
      category: "clinical",
      type: "video",
      icon: <PlayCircle className="text-rose-500" size={24} />,
      desc: "Use o construtor visual nativo ou seu papel timbrado do Canva para gerar PDFs de prescrições e contratos.",
      link: "/ajuda/tutoriais/documentos"
    },
    {
      title: "Criando e Enviando Anamneses",
      category: "clinical",
      type: "video",
      icon: <PlayCircle className="text-rose-500" size={24} />,
      desc: "Crie modelos personalizados e envie formulários remotos para que o paciente preencha antes da consulta.",
      link: "/ajuda/tutoriais/anamnese"
    },
    {
      title: "Galeria de Imagens e Arquivos Clínicos",
      category: "clinical",
      type: "article",
      icon: <Image className="text-indigo-500" size={24} />,
      desc: "Envie imagens clínicas, exames em PDF e modelos 3D STL para centralizar toda a documentação visual dos pacientes.",
      link: "/ajuda/tutoriais/imagens"
    },   
    {
      title: "Prontuários dos Pacientes",
      category: "clinical",
      type: "article",
      icon: <Stethoscope className="text-indigo-500" size={24} />,
      desc: "Aprenda a utilizar o prontuário completo dos pacientes, incluindo anamnese, odontograma, evolução clínica, imagens, documentos, financeiro e IA.",
      link: "/ajuda/tutoriais/prontuarios"
    },
    {
      title: "Alinhadores Invisíveis",
      category: "clinical",
      type: "article",
      icon: <Smile className="text-indigo-500" size={24} />,
      desc: "Gerencie tratamentos com alinhadores invisíveis, acompanhe etapas, refinamentos, histórico clínico e utilize a integração completa com prontuário, agenda e evolução clínica.",
      link: "/ajuda/tutoriais/alinhadores"
    },  
    {
      title: "Dashboard Executivo",
      category: "management",
      type: "article",
      icon: <LayoutDashboard className="text-indigo-500" size={24} />,
      desc: "Conheça o painel inteligente, acompanhe prioridades, personalize a visualização e utilize o Assistente Executivo e o Consultor de Gestão.",
      link: "/ajuda/tutoriais/dashboard"
    },
    {
      title: "Financeiro da Clínica",
      category: "management",
      type: "article",
      icon: <Receipt className="text-indigo-500" size={24} />,
      desc: "Controle faturas, recebimentos, despesas, parceiros, NFS-e, fluxo de caixa, indicadores financeiros e relatórios gerenciais.",
      link: "/ajuda/tutoriais/financeiro"
    },  
    {
      title: "Programa de Benefícios",
      category: "management",
      type: "article",
      icon: <GraduationCap className="text-indigo-500" size={24} />,
      desc: "Solicite benefícios acadêmicos, acompanhe a análise, renove quando permitido e entenda as regras para estudantes e recém-formados.",
      link: "/ajuda/tutoriais/programa-beneficios"
    },    
    {
      title: "Controle de Estoque",
      category: "management",
      type: "article",
      icon: <Package className="text-indigo-500" size={24} />,
      desc: "Controle materiais, movimentações, fornecedores e validade dos itens da clínica.",
      link: "/ajuda/tutoriais/estoque"
    },       
    {
      title: "Instalando e usando a Extensão do WhatsApp Web",
      category: "integrations",
      type: "article",
      icon: <Puzzle className="text-indigo-500" size={24} />,
      desc: "Aprenda a instalar, fixar e usar a extensão no Chrome e no Edge para integrar o atendimento no WhatsApp Web ao OdontoAssist.",
      link: "/ajuda/tutoriais/extensao-whatsapp"
    }
  ];

  const filteredTutorials =
    activeTab === "all"
      ? tutorials
      : tutorials.filter((t) => t.category === activeTab);

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-16">
        <div className="mb-12">
          <div className="flex items-center gap-2 text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-4">
            <Link to="/ajuda" className="hover:underline">Ajuda</Link>
            <ChevronRight size={14} className="text-gray-400" />
            <span>Guias</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">Tutoriais e Guias</h1>
              <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
                Explore nossa base de conhecimento para dominar todas as funcionalidades do OdontoAssist AI.
              </p>
            </div>
            <div className="relative w-full md:w-72 mt-4 md:mt-0">
              <input
                type="text"
                placeholder="Buscar tutorial..."
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 focus:outline-none transition-all"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-10">
          <div className="md:w-64 shrink-0">
            <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm sticky top-28">
              <h3 className="font-bold text-gray-900 mb-4 px-2 uppercase tracking-wide text-xs">Categorias</h3>
              <nav className="space-y-1">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveTab(cat.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                      activeTab === cat.id
                        ? "bg-indigo-50 text-indigo-600"
                        : "text-gray-600 hover:bg-slate-50 hover:text-gray-900"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="flex-1">
            <div className="grid sm:grid-cols-2 gap-6">
              {filteredTutorials.map((tut, idx) => (
                <Link
                  to={tut.link || "#"}
                  key={idx}
                  className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-indigo-100 transition-all cursor-pointer group flex flex-col"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-50 group-hover:scale-110 transition-transform">
                      {tut.icon}
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400 bg-slate-50 px-3 py-1 rounded-full">
                      {tut.type === "video" ? "Vídeo" : "Artigo"}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors">
                    {tut.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm mb-6 flex-1">
                    {tut.desc}
                  </p>
                  <div className="text-indigo-600 font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Acessar guia <ChevronRight size={16} />
                  </div>
                </Link>
              ))}
            </div>

            {filteredTutorials.length === 0 && (
              <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
                <FileText className="text-gray-300 mx-auto mb-4" size={48} />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Nenhum tutorial encontrado</h3>
                <p className="text-gray-500">Tente buscar por outros termos ou categorias.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
